import { type ReactNode, useRef } from "react";
import type { ClientsWorkOverviewSortValue } from "@common/types/views/clientsWorkOverviewView";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { useDynamicTopOffset } from "@design-system/hooks/useDynamicTopOffset";
import { Loader } from "@design-system/Loader/Loader";
import { styleWithVars } from "@lib/cssVars";
import type { ClientsWorkOverviewCallbacks } from "../WorkRowActions";
import type { WorkBoardState } from "./useWorkBoardLayout";
import { WorkBoard, type WorkBoardProps } from "./WorkBoard";
import styles from "./WorkBoardContent.module.scss";

interface WorkBoardContentProps extends Pick<
    WorkBoardProps,
    "activeItemId" | "onOpenItem"
> {
    board: WorkBoardState["board"];
    /** The page failed to load: the error with Retry replaces the board. */
    isError: boolean;
    onRetry: () => void;
    sort: ClientsWorkOverviewSortValue | undefined;
    /** List's row action callbacks, for the cards' context menus. */
    callbacks: ClientsWorkOverviewCallbacks | undefined;
    /** Shown centered when there are no applications at all. */
    emptyView: ReactNode;
    /** The command island, floating over the bottom of the board. */
    footer: ReactNode;
}

/** What Board shows under the page header: the error, a loader, the empty view, or the board. */
export const WorkBoardContent = ({
    board,
    isError,
    onRetry,
    sort,
    callbacks,
    activeItemId,
    onOpenItem,
    emptyView,
    footer,
}: Readonly<WorkBoardContentProps>) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { topOffset } = useDynamicTopOffset({ containerRef });

    const renderContent = () => {
        if (isError) {
            return (
                <div className={styles.centered} role="alert">
                    <EmptyState
                        header="Failed to load work items"
                        ctaText="Retry"
                        action={onRetry}
                        buttonVariant="secondary"
                    />
                </div>
            );
        }
        if (board === null) {
            return (
                <div className={styles.centered}>
                    <Loader />
                </div>
            );
        }
        if (board.rows.length === 0) {
            return <div className={styles.centered}>{emptyView}</div>;
        }
        return (
            <WorkBoard
                {...board}
                sort={sort}
                callbacks={callbacks}
                activeItemId={activeItemId}
                onOpenItem={onOpenItem}
            />
        );
    };

    return (
        <div
            ref={containerRef}
            className={styles.root}
            style={styleWithVars({
                "--work-board-top-offset": `${topOffset}px`,
            })}
        >
            <div className={styles.board}>{renderContent()}</div>
            {footer && <div className={styles.island}>{footer}</div>}
        </div>
    );
};
